<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';
import { useSidebarContext } from './provider';
import { useMapGetter } from 'dashboard/composables/store';

const { isCollapsed } = useSidebarContext();
const accountId = useMapGetter('getCurrentAccountId');

const isOpen = ref(false);
const iframeUrl = ref('');
const errorMessage = ref('');

const openRadar = async () => {
  if (!accountId.value) return;
  isOpen.value = true;
  iframeUrl.value = '';
  errorMessage.value = '';
  try {
    const res = await fetch(
      `/_panel/api/kanban/${accountId.value}/embed-url`,
      { credentials: 'include' }
    );
    const data = await res.json();
    if (data.url) {
      iframeUrl.value = data.url;
    } else {
      errorMessage.value = data.error || 'Radar não disponível para esta conta.';
    }
  } catch (e) {
    errorMessage.value = e.message;
  }
};

const closeRadar = () => {
  isOpen.value = false;
  iframeUrl.value = '';
  errorMessage.value = '';
};

const onKey = e => {
  if (e.key === 'Escape') closeRadar();
};

watch(isOpen, value => {
  if (value) {
    document.addEventListener('keydown', onKey);
  } else {
    document.removeEventListener('keydown', onKey);
  }
});

onUnmounted(() => {
  document.removeEventListener('keydown', onKey);
});

const buttonTitle = computed(() => 'Radar');
</script>

<template>
  <li class="grid gap-1 text-sm cursor-pointer select-none min-w-0 list-none">
    <button
      v-if="isCollapsed"
      type="button"
      :title="buttonTitle"
      class="flex items-center justify-center size-10 rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
      @click="openRadar"
    >
      <span class="i-lucide-radar size-4" />
    </button>
    <button
      v-else
      type="button"
      :title="buttonTitle"
      class="flex items-center w-full gap-2 px-1.5 py-1 rounded-lg text-n-slate-11 hover:bg-n-alpha-2 h-8 min-w-0"
      @click="openRadar"
    >
      <span class="i-lucide-radar size-4 flex-shrink-0" />
      <span class="flex-1 truncate min-w-0 text-start text-body-main">Radar</span>
    </button>
  </li>

  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[99999] flex items-center justify-center p-6 bg-n-slate-12/55 backdrop-blur-sm"
      @click.self="closeRadar"
    >
      <div
        class="relative w-full h-full max-w-[min(96vw,2000px)] bg-n-background rounded-xl shadow-2xl overflow-hidden flex flex-col"
      >
        <button
          type="button"
          aria-label="Fechar"
          title="Fechar (Esc)"
          class="absolute top-2.5 right-10 size-8 rounded-full bg-n-slate-12/85 text-n-slate-1 flex items-center justify-center z-10 hover:bg-n-slate-12"
          @click="closeRadar"
        >
          <span class="i-lucide-x size-4" />
        </button>
        <iframe
          v-if="iframeUrl"
          :src="iframeUrl"
          class="flex-1 w-full h-full border-0 bg-n-background"
        />
        <div
          v-else-if="errorMessage"
          class="flex items-center justify-center flex-1 p-8 text-n-ruby-11"
        >
          {{ errorMessage }}
        </div>
        <div
          v-else
          class="flex items-center justify-center flex-1 p-8 text-n-slate-11"
        >
          Carregando Radar…
        </div>
      </div>
    </div>
  </Teleport>
</template>
